import { motion } from "framer-motion";
import { Star, Quote } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";

const EASE = [0.22, 1, 0.36, 1];

const MEMBERS = [
  { name: "Emily", role: "ON member · Trustpilot review, Aug 2026", quote: "Most valuable subscription I own. Having almost instant access to legal advice & help has been lifesaving to me on numerous occasions! My legal advisor is always extremely honest, realistic, & helpful while also being kind, considerate, & empathetic." },
  { name: "Carol", role: "TX member · Trustpilot review, Aug 2026", quote: "I have been a long time member. I have received the best advice and guidance. The response to requests is quick. It is invaluable to have ongoing access to these services." },
  { name: "Brad", role: "AL member · Trustpilot review, Jul 2026", quote: "What stood out the most to me was knowing that when life gets difficult, you have someone you can call who will actually help guide you through the situation calmly and professionally. That peace of mind means a lot." },
];

const ASSOCIATES = [
  { name: "James K.", role: "Associate, 2 years", quote: "I started part-time around my day job. The training walked me through everything, and the membership renewals mean my work keeps paying off." },
  { name: "Priya S.", role: "Associate, 1 year", quote: "What sold me was that I'd actually use the product myself. Talking about it never feels like selling — it feels like recommending." },
  { name: "Rob D.", role: "Associate, 4 years", quote: "The back office handles the unglamorous parts. I spend my time having conversations and helping people. That balance is why I stayed." },
];

const reveal = {
  hidden: { opacity: 0, y: 36 },
  show: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.7, ease: EASE } }),
};

function Card({ item, i }) {
  return (
    <motion.figure
      variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-60px" }} custom={i}
      className="rounded-3xl border border-slate-200 bg-white p-8 flex flex-col transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:shadow-blue-100"
      data-testid={`testimonial-card-${i}`}
    >
      <Quote size={22} className="text-blue-200 mb-5" />
      <blockquote className="text-slate-700 leading-relaxed flex-1">{item.quote}</blockquote>
      <figcaption className="mt-7 flex items-center justify-between">
        <div>
          <p className="font-display font-bold text-[#0B192C]">{item.name}</p>
          <p className="text-xs text-slate-500 mt-0.5">{item.role}</p>
        </div>
        <div className="flex gap-0.5 text-amber-400">
          {[...Array(5)].map((_, s) => <Star key={s} size={13} fill="currentColor" strokeWidth={0} />)}
        </div>
      </figcaption>
    </motion.figure>
  );
}

export default function Testimonials() {
  return (
    <section id="stories" className="bg-[#F8FAFC] border-y border-slate-200 py-24 md:py-36 scroll-mt-20" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12">
        <motion.p variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={0}
          className="font-mono-accent text-xs uppercase tracking-[0.28em] text-blue-600 mb-6">
          Two kinds of wins
        </motion.p>
        <motion.h2 variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true, margin: "-80px" }} custom={1}
          className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-[#0B192C] max-w-2xl leading-[1.1] mb-14">
          Members sleep better. Associates build something.
        </motion.h2>

        <Tabs defaultValue="members" className="w-full">
          <TabsList data-testid="testimonial-tabs" className="bg-white border border-slate-200 rounded-full p-1 h-auto mb-10">
            <TabsTrigger data-testid="tab-members" value="members" className="rounded-full px-6 py-2.5 text-sm font-semibold data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white">
              Member stories
            </TabsTrigger>
            <TabsTrigger data-testid="tab-associates" value="associates" className="rounded-full px-6 py-2.5 text-sm font-semibold data-[state=active]:bg-[#1D4ED8] data-[state=active]:text-white">
              Associate stories
            </TabsTrigger>
          </TabsList>
          <TabsContent value="members">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {MEMBERS.map((m, i) => <Card key={m.name} item={m} i={i} />)}
            </div>
          </TabsContent>
          <TabsContent value="associates">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {ASSOCIATES.map((m, i) => <Card key={m.name} item={m} i={i + 3} />)}
            </div>
          </TabsContent>
        </Tabs>
        <p className="text-xs text-slate-400 mt-8" data-testid="testimonials-caption">Member stories are real reviews published on Trustpilot and featured on the company's official website. Associate stories are illustrative examples. Individual experiences and results vary.</p>
      </div>
    </section>
  );
}
